import { useState, useEffect } from 'react'
import { NavLink } from 'react-router-dom'
import {
  LayoutDashboard,
  Cpu,
  TrendingUp,
  GitCompare,
  Search,
  Wrench,
  Brain,
  BarChart3,
  Settings,
  HelpCircle,
  ChevronLeft,
  ChevronRight
} from 'lucide-react'
import clsx from 'clsx'

interface NavItem {
  path: string
  label: string
  icon: React.ReactNode
  badge?: string
}

const navItems: NavItem[] = [
  { path: '/', label: 'Command Center', icon: <LayoutDashboard size={20} /> },
  { path: '/assets', label: 'Asset Intelligence', icon: <Cpu size={20} /> },
  { path: '/predictions', label: 'Predictive Ops', icon: <TrendingUp size={20} /> },
  { path: '/compare', label: 'Comparative', icon: <GitCompare size={20} /> },
  { path: '/root-cause', label: 'Root Cause', icon: <Search size={20} /> },
  { path: '/maintenance', label: 'Maintenance', icon: <Wrench size={20} /> },
  { path: '/learning', label: 'Self-Learning', icon: <Brain size={20} /> },
  { path: '/executive', label: 'Executive', icon: <BarChart3 size={20} /> },
]

const bottomNavItems: NavItem[] = [
  { path: '/settings', label: 'Settings', icon: <Settings size={20} /> },
  { path: '/help', label: 'Help', icon: <HelpCircle size={20} /> },
]

const SIDEBAR_COLLAPSED_KEY = 'sme-dashboard-sidebar-collapsed'

export default function Sidebar() {
  const [isCollapsed, setIsCollapsed] = useState(() => {
    try {
      const stored = localStorage.getItem(SIDEBAR_COLLAPSED_KEY)
      return stored === 'true'
    } catch {
      return false
    }
  })

  // Persist collapsed state
  useEffect(() => {
    try {
      localStorage.setItem(SIDEBAR_COLLAPSED_KEY, String(isCollapsed))
    } catch {
      // Ignore localStorage errors
    }
  }, [isCollapsed])

  const toggleCollapsed = () => setIsCollapsed(!isCollapsed)

  return (
    <aside
      className={clsx(
        'bg-surface-900 border-r border-surface-700 flex flex-col transition-all duration-300 ease-in-out',
        isCollapsed ? 'w-16' : 'w-64'
      )}
    >
      {/* Logo */}
      <div className="h-16 flex items-center px-4 border-b border-surface-700">
        <div className="flex items-center gap-3">
          <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-primary-500 to-accent-cyan flex items-center justify-center flex-shrink-0">
            <span className="text-white font-bold text-sm">AI</span>
          </div>
          {!isCollapsed && (
            <div className="overflow-hidden">
              <h1 className="text-surface-100 font-semibold text-sm whitespace-nowrap">SME Dashboard</h1>
              <p className="text-surface-500 text-xs whitespace-nowrap">Asset Intelligence</p>
            </div>
          )}
        </div>
      </div>

      {/* Navigation */}
      <nav className="flex-1 py-4 px-2 overflow-y-auto">
        <div className="space-y-1">
          {navItems.map((item) => (
            <NavLink
              key={item.path}
              to={item.path}
              title={isCollapsed ? item.label : undefined}
              className={({ isActive }) =>
                clsx(
                  'flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-all duration-200',
                  isCollapsed && 'justify-center px-2',
                  isActive
                    ? 'bg-primary-600/20 text-primary-400 border-l-2 border-primary-500'
                    : 'text-surface-400 hover:text-surface-200 hover:bg-surface-800'
                )
              }
            >
              <span className="flex-shrink-0">{item.icon}</span>
              {!isCollapsed && (
                <>
                  <span className="truncate">{item.label}</span>
                  {item.badge && (
                    <span className="ml-auto badge badge-info">{item.badge}</span>
                  )}
                </>
              )}
            </NavLink>
          ))}
        </div>
      </nav>

      {/* Bottom Navigation */}
      <div className="py-4 px-2 border-t border-surface-700">
        <div className="space-y-1">
          {bottomNavItems.map((item) => (
            <NavLink
              key={item.path}
              to={item.path}
              title={isCollapsed ? item.label : undefined}
              className={({ isActive }) =>
                clsx(
                  'flex items-center gap-3 px-3 py-2 rounded-lg text-sm font-medium transition-all duration-200',
                  isCollapsed && 'justify-center px-2',
                  isActive
                    ? 'bg-surface-800 text-surface-200'
                    : 'text-surface-500 hover:text-surface-300 hover:bg-surface-800'
                )
              }
            >
              <span className="flex-shrink-0">{item.icon}</span>
              {!isCollapsed && <span className="truncate">{item.label}</span>}
            </NavLink>
          ))}
        </div>
      </div>

      {/* Toggle Button & Version Info */}
      <div className="px-2 py-3 border-t border-surface-700">
        <button
          onClick={toggleCollapsed}
          className="w-full flex items-center justify-center gap-2 px-3 py-2 rounded-lg text-surface-500 hover:text-surface-300 hover:bg-surface-800 transition-colors"
          title={isCollapsed ? 'Expand sidebar' : 'Collapse sidebar'}
        >
          {isCollapsed ? <ChevronRight size={18} /> : <ChevronLeft size={18} />}
          {!isCollapsed && <span className="text-xs">Collapse</span>}
        </button>
        {!isCollapsed && (
          <p className="text-xs text-surface-600 text-center mt-2">
            v2.0.0 | 56 Assets
          </p>
        )}
      </div>
    </aside>
  )
}
